import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, EventEmitter, inject, input, OnDestroy, Output, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColDef, IAggFuncParams, ValueGetterParams } from 'ag-grid-community'; // Column Definition Type Interface
import { GridApi, GridOptions, IGroupCellRendererParams, IRowNode, SideBarDef } from "ag-grid-enterprise";
import { WorkOrderBase } from 'src/app/CommonFiles/BusinessModels';
import { ColumnSettings } from 'src/app/CommonFiles/UIModels';
import { SelectedRowsFilter } from '../selected-filter/selected-filter';
import { DetailPageRenderer } from '../row-details/row-details';
import { State } from '@progress/kendo-data-query';
import { BlackoutCountdownDaysCellRenderer } from '../row-blackout-countdown-days/row-blackout-countdown-days';
import { PiiMaskCellRenderer } from '../../../shared/pii-mask/pii-mask-cell-renderer.component';
import { PiiMaskInnerHeaderComponent } from '../../../shared/pii-mask/pii-mask-inner-header.component';
import { isPiiField, getColumnCategory, getPiiGroupKeysFromColumns } from '../../../shared/pii-mask/pii-fields.config';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { PiiMaskService } from 'src/app/shared/services/pii-mask.service';
import { TranslateService } from '@ngx-translate/core';
import { buildAgGridLocaleText } from 'src/app/shared/ag-grid/ag-grid-locale-text';
import { WorkorderGRPCService } from 'src/app/ProxyServices/workorder.grpc.service';
import { WorkOrderModel } from 'src/app/CommonFiles/BusinessModels';

export interface AgGridState {
  colsCount: number;
  rowsCount: number;
  grouping: boolean;
  filtering: boolean;
  pivot: boolean;
}

@Component({
  standalone: false,
  selector: 'app-ag-grid',
  templateUrl: './ag-grid.component.html',
  styleUrl: './ag-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AGGridComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);

  // agGridAPI:GridApi;
  agGridAPI: WritableSignal<GridApi> = signal(null);
  isPivotEnabled: WritableSignal<boolean> = signal(false);

  workOrders = input<any[]>([]);
  readOnlyMode = input<boolean>(true);
  columnsList = input<ColumnSettings[]>([]);
  gridState = input<State>();
  pending = input<boolean>();

  @Output() updateColumnsList = new EventEmitter<ColumnSettings[]>();

  isPiiRevealed = false;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  private readonly destroyRef = inject(DestroyRef);

  agToKendoOperatorMap = {
    'contains': 'contains',
    'or': 'or',
    'and': 'and',
    'notContains': 'doesnotcontain',
    'equals': 'eq',
    'notEqual': 'neq',
    'startsWith': 'startswith',
    'endsWith': 'endswith',
    'blank': 'isnull',
    'notBlank': 'isnull',
    'greaterThan': 'gt',
    'lessThan': 'lt',
  }

  kendoToAGOperatorMap = {
    'contains': 'contains',
    'or': 'or',
    'and': 'and',
    'doesnotcontain': 'notContains',
    'eq': 'equals',
    'neq': 'notEqual',
    'startsWith': 'startswith',
    'endsWith': 'endswith',
    'isnull': 'blank',
    'gt': 'greaterThan',
    'lt': 'lessThan',
    'gte': 'greaterThan',
    'lte': 'lessThan',
  }

  @Output() updateWOSelection = new EventEmitter<WorkOrderBase[]>();
  @Output() updateState = new EventEmitter<State>();

  public sideBar: SideBarDef | string | string[] | boolean | null = ["filters", "columns"];
  public rowGroupPanelShow: "always" | "onlyWhenGrouping" | "never" = "always";
  public localeText = buildAgGridLocaleText(this.translate);

  preColDefs: ColDef<any>[] = [
    {
      headerName: this.translate.instant('HOME_COMPONENTS.AG_GRID.ROW_NUMBER'),
      valueGetter: 'node.rowIndex + 1',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      filter: SelectedRowsFilter,
      enableRowGroup: false,
      sortable: true,
      headerCheckboxSelectionFilteredOnly: true,
      minWidth: 120,
      colId: 'grid-index',
    },
  ];

  preColDefsWithSelection: ColDef<any>[] = [
    {
      headerName: this.translate.instant('HOME_COMPONENTS.AG_GRID.ROW_NUMBER'),
      valueGetter: 'node.rowIndex + 1',
      headerCheckboxSelection: false,
      filter: false,
      sortable: true,
      minWidth: 120,
      colId: 'grid-index'
    },
  ];

  prePivotColDefs: ColDef<any>[] = [
    {
      headerName: this.translate.instant('HOME_COMPONENTS.AG_GRID.WORK_ORDER_ID'),
      minWidth: 120,
      colId: 'WorkOrderId',
      aggFunc: "count",
      enableValue: true,
      field: "WorkOrderId",
      enableRowGroup: false,
      enablePivot: false
    }
  ];


  // cols computed values 
  columns = computed<any>(() => {
    let columnsList: any = this.columnsList();
    columnsList.sort((a, b) => a.title.localeCompare(b.title));
    columnsList = columnsList.map((col) => {
      const field = col.field;
      const category = getColumnCategory(col);
      // colDef 
      const colDef: ColDef = {
        hide: col.hidden,
        headerName: col.title,
        colId: col.field,
        filterParams: {
          buttons: ['apply', 'reset'],
          applyButton: true,
          closeOnApply: true,
        },
        field: ColumnSettings.toAgField(col)
      };
      if (field === 'AssignedTo') {
        return {
          ...colDef,
          headerValueGetter: () => this.translate.instant('HOME_COMPONENTS.AG_GRID.ASSIGNED_TO'),
          headerName: this.translate.instant('HOME_COMPONENTS.AG_GRID.ASSIGNED_TO_GROUP'),
          aggFunc: (params: IAggFuncParams) => {
            const values = params.values;
            if (!values || values.length === 0) return null;
            const first = values[0];
            return values.every(v => v === first) ? first : null;
          },
          valueGetter: (params: ValueGetterParams) => {
            if (params.node.group) {
              // Use aggregation value from the group
              return params.node.aggData?.[params.colDef.field as string] ?? null;
            }
            return params.data?.AssignedTo ?? null;
          }
        }
      }
      if (field === 'WorkOrderId') {
        return {
          ...colDef,
          cellRenderer: DetailPageRenderer,
          filter: "agTextColumnFilter",
        }
      }
      // PII Masking Logic - use shared config
      if (isPiiField(field, category)) {
        return {
          ...colDef,
          headerComponent: PiiMaskInnerHeaderComponent,
          headerComponentParams: { piiGroupKey: field },
          cellRenderer: PiiMaskCellRenderer,
          cellRendererParams: { group: field },
          enableRowGroup: true,
          filter: "agTextColumnFilter"
        }
      }

      if (field === 'BlackoutRemainingDays') {
        return {
          ...colDef,
          cellRenderer: BlackoutCountdownDaysCellRenderer,
          filter: "agTextColumnFilter"
        }
      }
      if (col.filter === 'date') {
        return {
          ...colDef,
          enableRowGroup: true,
          filter: "agDateColumnFilter", // Requires filter type predefined for colDefs
          filterParams: {
            buttons: ['apply', 'reset'],
            applyButton: true,
            closeOnApply: true,
            debounceMs: 0, // set debounce to 0 to avoid delay
            comparator: this.dateComparator
          },
        }
      }
      if (col.filter === 'boolean') {
        return {
          ...colDef,
          cellRenderer: (params: any) => (params.value ? 'true' : 'false'),
        }
      }
      return {
        ...colDef,
        enableRowGroup: true,
        filter: "agTextColumnFilter", // Requires filter type predefined for colDefs
      };
    });
    let preCols = this.readOnlyMode() ? this.preColDefsWithSelection : this.preColDefs;
    // While streaming, suppress the select-all header checkbox so the user
    // cannot trigger bulk selection against a still-loading dataset.
    if (this.workorderService.streamingInProgress() && !this.readOnlyMode()) {
      preCols = this.preColDefsWithSelection;
    }
    // if pivot enabled change preCols with Count column
    if (this.isPivotEnabled()) {
      preCols = this.prePivotColDefs;
      const pivotRemoveCols = ["WorkOrderId", "Address", "Latitude", "Longitude", "Email", "CellPhoneNo", "CustomerName"];
      let filteredCols = columnsList.filter(item => !pivotRemoveCols.includes(item.colId) && item.hide === false);
      const cols = [...preCols, ...filteredCols];
      return cols;
    } else {
      const cols = [...preCols, ...columnsList];
      return cols;
    }
  });

  /**
   * Remembers the user's pivot intent at stream-start so we can restore it
   * when the stream ends. `null` means "no active stream is hiding pivot."
   */
  private pivotIntentBeforeStream: boolean | null = null;


  selectedWorkOrders = input<WorkOrderBase[]>([]);

  selectedSet = computed<Set<string>>(() => {
    const selectedSet = new Set(this.selectedWorkOrders().map(feature => feature.name));
    return selectedSet;
  });

  gridOptions: GridOptions = {
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
        { statusPanel: 'agTotalRowCountComponent' },
        { statusPanel: 'agFilteredRowCountComponent' },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agAggregationComponent' }
      ]
    },
    rowSelection: "multiple",
    groupSelectsChildren: true,
    suppressRowClickSelection: true,
    suppressAggFuncInHeader: true,
    getRowId: (params) => params.data?.name,
  };

  themeClass = "ag-theme-quartz";
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 200,
    enableRowGroup: true,
    enablePivot: true,
  };

  /**
   * rowData binding source. During streaming we return a stable empty array
   * so the grid does not rebuild on every snapshot update from `next()` —
   * rows are owned by `applyTransactionAsync` (see `applyStreamChunk`).
   * Once streaming ends, the binding switches to the raw `workOrders()` input
   * and the grid reconciles to the final list. AG Grid ignores properties
   * not referenced by `colDef.field`, so no pre-filtering is needed.
   */
  private readonly EMPTY_ROW_DATA: any[] = [];
  streamAwareRowData = computed<any[]>(() => {
    if (this.workorderService.streamingInProgress()) {
      return this.EMPTY_ROW_DATA;
    }
    return this.workOrders();
  });
  public autoGroupColumnDef: ColDef = {
    minWidth: 200,
    cellRenderer: "agGroupCellRenderer",
    cellRendererParams: {
      checkbox: true,
      // suppressCount: true
    } as IGroupCellRendererParams,
  };

  getMainMenuItems = (params: any) => {
    const defaultItems = params.defaultItems;

    // Filter out "Value Aggression" (its key is 'aggFunc')
    return defaultItems.filter(item => (item !== 'aggFunc') && (item !== 'valueAggSubMenu'));
  };
  /**
   * Buffer for chunks that arrived before the grid API is ready. Once
   * `onGridReady` fires, these are replayed in order via applyTransactionAsync.
   */
  private pendingChunks: WorkOrderModel[][] = [];

  constructor(
    private piiService: PiiMaskService,
    private cdr: ChangeDetectorRef,
    public workorderService: WorkorderGRPCService
  ) {
    this.applyTranslations();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyTranslations());

    // ─── Progressive streaming ──────────────────────────────────────────
    // On stream start (new request, possibly superseding a prior in-flight
    // stream): drop any buffered chunks, drain queued async transactions, and
    // clear visible rows so the grid doesn't show a flash of stale data.
    this.workorderService.RXStreamStart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pendingChunks = [];
        const api = this.agGridAPI();
        if (api && !(api as any).isDestroyed?.()) {
          api.flushAsyncTransactions?.();
          api.setGridOption('rowData', []);
        }
      });

    // Subscribe to incremental row chunks from the gRPC-web stream. AG Grid
    // appends them via `applyTransactionAsync`, which coalesces multiple
    // transactions into one animation-frame layout pass — keeps the grid
    // responsive even while 30k rows are arriving.
    this.workorderService.RXStreamChunks
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((chunk) => this.applyStreamChunk(chunk));

    // On stream end, flush any pending async transactions so the row count
    // is fully reconciled before any "stream complete" UI fires.
    this.workorderService.RXStreamEnd
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.agGridAPI()?.flushAsyncTransactions?.());

    // Pivot defer: pivot computation is O(rows × dimensions) and visibly
    // chaotic when re-run on every chunk. While the stream is in flight we
    // force pivot mode off and restore the user's previous setting at end.
    effect(() => {
      const streaming = this.workorderService.streamingInProgress();
      const api = this.agGridAPI();
      if (!api || (api as any).isDestroyed?.()) return;

      if (streaming && this.pivotIntentBeforeStream === null) {
        // Stream just started — snapshot current pivot state and disable it.
        this.pivotIntentBeforeStream = api.isPivotMode();
        if (this.pivotIntentBeforeStream) {
          api.setGridOption('pivotMode', false);
        }
      } else if (!streaming && this.pivotIntentBeforeStream !== null) {
        // Stream ended — restore the user's previous pivot intent.
        if (this.pivotIntentBeforeStream) {
          api.setGridOption('pivotMode', true);
        }
        this.pivotIntentBeforeStream = null;
      }
    });

    // Derive icon state from service — no local timer needed
    this.piiService.groupStates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const piiGroups = getPiiGroupKeysFromColumns(this.columnsList());
        this.isPiiRevealed = this.piiService.isAnyRevealed(piiGroups);
        this.cdr.markForCheck();
      });

    // Selection effect
    effect(() => {
      if (this.selectedSet()) {
        if (this.agGridAPI() !== null) {

          const nodesToSelect: IRowNode[] = [];
          const nodesToDeSelect: IRowNode[] = [];
          this.agGridAPI().forEachNode((node: IRowNode) => {
            const data = node.data;
            if (data) {
              const workOrderName = data.name;
              const isSelected = node.isSelected();
              if (this.selectedSet().has(workOrderName)) {
                if (isSelected === false) {
                  nodesToSelect.push(node);
                }
              } else {
                if (isSelected) {
                  nodesToDeSelect.push(node);
                }
              }
            }
          });
          // update all selected rows;
          if (nodesToSelect.length) {
            this.agGridAPI().setNodesSelected({ nodes: nodesToSelect, newValue: true });
          }
          // update all un selected rows;
          if (nodesToDeSelect.length) {
            this.agGridAPI().setNodesSelected({ nodes: nodesToDeSelect, newValue: false });
          }
        }

      }
    });

    // Filter mapping effect
    effect(() => {
      const state = this.gridState();
      if (state.filter?.filters?.length > 0) {
        const filters = this.gridState().filter.filters;
        let newF = {};
        filters.forEach((filter: any) => {
          const innerFilter = filter.filters;
          let newFilter = [];
          let field;
          innerFilter.forEach((item: any) => {
            field = item.field;
            const fieldCol = this.columns().find((item) => item.colId === field);
            const filterType = fieldCol.filter;
            let payload: any = {
              type: this.kendoToAGOperatorMap[item.operator]
            }
            if (filterType === 'agTextColumnFilter') {
              payload = {
                ...payload,
                filter: item.value,
                filterType: 'text',
              };
            }
            if (filterType === 'agDateColumnFilter') {
              payload = {
                ...payload,
                dateFrom: this.formatDate(item.value),
                dateTo: null,
                filterType: 'date',
              };
            }
            newFilter.push(payload);
          });
          if (newFilter.length > 1) {
            field = newFilter[0].field;
            const fieldCol = this.columns().find((item) => item.colId === field);
            const agGridFilterType = fieldCol.filter;
            let filterType = 'text';
            if (agGridFilterType === 'agDateColumnFilter') {
              filterType = 'date';
            }
            const t = {
              filterType,
              operator: "AND",
              conditions: newFilter
            };
            newF[field] = t;
          } else if (newFilter.length === 1) {
            newF[field] = newFilter[0];
          }
        });
        if (this.agGridAPI()) {
          this.agGridAPI().setFilterModel(newF);
        }
      } else {
        if (this.agGridAPI()) {
          this.agGridAPI().setFilterModel(null);
        }
      }
    });
  }

  private applyTranslations(): void {
    this.localeText = buildAgGridLocaleText(this.translate);
    this.gridOptions = { ...this.gridOptions, localeText: this.localeText };
    this.agGridAPI()?.refreshHeader();
  }

  ngOnDestroy() {
    if (this.agGridAPI().isDestroyed() === false) {
      this.agGridAPI()?.setGridOption("columnDefs", []);
    }
    this.agGridAPI()?.destroy();
    this.agGridAPI.set(null);
  }
  onGridReady(ev) {
    this.agGridAPI.set(ev.api);
    // Replay any chunks that arrived before the grid API was ready.
    if (this.pendingChunks.length > 0) {
      for (const chunk of this.pendingChunks) {
        ev.api.applyTransactionAsync({ add: chunk });
      }
      this.pendingChunks = [];
    }
  }

  /**
   * Append a chunk of streamed rows to the grid. Falls back to buffering
   * when the grid API isn't ready yet (component template `*ngIf` may keep
   * AG Grid unmounted until the first chunk emits via `next()`).
   */
  private applyStreamChunk(chunk: WorkOrderModel[]): void {
    if (!chunk || chunk.length === 0) return;
    const api = this.agGridAPI();
    if (!api || (api as any).isDestroyed?.()) {
      this.pendingChunks.push(chunk);
      return;
    }
    api.applyTransactionAsync({ add: chunk });
  }

  onSelectionChanged(e: any) {
    const selectedRows = e.api.getSelectedRows();
    if (selectedRows.length !== this.selectedWorkOrders().length) {
      this.updateWOSelection.emit(selectedRows);
    }
  }

  rowGroup(ev) {
    if (this.agGridAPI() !== null) {
      const cols = this.agGridAPI().getRowGroupColumns();
      if (cols.length > 0) {
        this.agGridAPI().setColumnsVisible(['grid-index'], false);
      } else {
        this.agGridAPI().setColumnsVisible(['grid-index'], true);
      }
    }
  }
  columnVisible(ev) {
    const colsMap = new Set(ev.columns.map(({ colId }) => colId));
    const hidden = !ev.visible;
    const source = ev.source;

    // 60483 Dragging cols for grouping or during pivot calls the backend we need to avoid that
    if (source !== 'uiColumnDragged' && this.isPivotEnabled() === false) {
      let columnsList = this.columnsList().map((item) => {
        return colsMap.has(item.field) ? { ...item, hidden } : { ...item }
      });
      this.updateColumnsList.emit(columnsList);
    }
  }

  filterChange(ev) {
    const filters = this.agGridAPI().getFilterModel();

    const keys = Object.keys(filters); // ['name', 'age', 'city']
    let kendoGridFilters = [];
    keys.forEach((field) => {
      let agGridFilter = filters[field];

      let kendoGridFilter = [];
      const conditions = agGridFilter.conditions?.length > 0 ? agGridFilter.conditions : [];
      if (conditions.length > 0) {

        conditions.forEach(element => {
          let value = element.filter;
          if (element.filterType === 'date') {
            // Normalize to the "YYYY-MM-DD" the backend day-truncation expects
            // (ola_date_between / ola_date_not_between concat ' 00:00:00' / ' 23:59:59').
            value = (element.dateFrom as string).slice(0, 10);
          }
          const payload = {
            field,
            operator: this.agToKendoOperatorMap[element.type],
            value
          }
          kendoGridFilter.push(payload);
        });
      } else {
        let value = agGridFilter.filter;
        if (agGridFilter.filterType === 'date') {
          value = (agGridFilter.dateFrom as string).slice(0, 10);
        }

        const payload = {
          field,
          operator: this.agToKendoOperatorMap[agGridFilter.type],
          value
        }
        kendoGridFilter.push(payload);
      };
      const filter = {
        logic: conditions.length > 0 ? agGridFilter.operator.toLowerCase() : 'and',
        filters: kendoGridFilter
      };
      kendoGridFilters.push(filter)
    });
    const newFilters: any = {
      logic: 'and',
      filters: kendoGridFilters
    }
    const state = {
      ...this.gridState(),
      filter: newFilters
    };
    this.updateState.emit(state);
  }

  pivotChange(ev) {
    const isEnabled = ev.api.isPivotMode();
    this.isPivotEnabled.set(isEnabled);

    if (isEnabled) {

      this.agGridAPI()?.setGridOption("grandTotalRow", "bottom");
      this.agGridAPI()?.setGridOption("groupTotalRow", "bottom");
      this.agGridAPI()?.setGridOption("pivotColumnGroupTotals", "after");


    } else {
      // ev.api.setRowSelection('multiple');
      const gridOptions: any = {
        grandTotalRow: undefined,
        groupTotalRow: undefined,
        groupSelectsChildren: true
      }
      this.agGridAPI()?.updateGridOptions(gridOptions);
    }
  }
  dateComparator(filterDate: Date, cellValue: string) {
    if (!cellValue) return -1;

    // Parse the cell date (adjust based on your date format)
    const cellDate = new Date(cellValue);

    // Clear time part for both dates
    filterDate.setHours(0, 0, 0, 0);
    cellDate.setHours(0, 0, 0, 0);

    if (filterDate.getTime() === cellDate.getTime()) {
      return 0;
    }
    return cellDate < filterDate ? -1 : 1;
  }
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  togglePii(): void {
    const piiGroups = getPiiGroupKeysFromColumns(this.columnsList());
    this.piiService.toggleGroups(piiGroups);
  }
}
